'use client'

import Link from 'next/link'
import { AppointmentBookingTrigger } from '@/app/components/appointments/AppointmentBookingTrigger'
import PageLayout from '@/app/components/common/PageLayout'
import Header from '@/app/components/common/Header'
import AppImage from '@/app/components/ui/AppImage'
import type { Service } from '@/lib/types/service.types'
import { PROSE_HTML_CLASS, sanitizeHtml, stripHtml } from '@/lib/utils/html'
import { Button } from '@/components/ui/button'

function formatPrice(price: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
  }).format(price)
}

export function ServiceDetailView({ service }: { service: Service }) {
  const imageUrl =
    service.image?.url ||
    service.image?.publicUrl ||
    service.service_images?.[0]?.url ||
    service.service_images?.[0]?.publicUrl

  return (
    <PageLayout>
      <Header variant="light" />
      <div className="mx-auto max-w-4xl px-4 py-10">
        {imageUrl ? (
          <div className="relative mb-8 h-64 w-full overflow-hidden rounded-xl md:h-80">
            <AppImage
              src={imageUrl}
              alt={service.service_name}
              fill
              className="object-cover"
            />
          </div>
        ) : null}
        <p className="text-sm text-primary">
          {service.service_category?.name ?? 'Service'}
        </p>
        <h1 className="mt-2 text-3xl font-bold text-foreground">
          {service.service_name}
        </h1>
        <p className="mt-4 text-lg font-semibold text-foreground">
          {formatPrice(Number(service.service_price))}
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            · {service.service_duration} min
          </span>
        </p>
        {service.service_description ? (
          <div
            className={`mt-6 ${PROSE_HTML_CLASS}`}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(service.service_description),
            }}
          />
        ) : (
          <p className="mt-6 text-muted-foreground">
            {stripHtml(service.service_name)} — professional hair care tailored to you.
          </p>
        )}
        <div className="mt-10 flex flex-wrap gap-3">
          <AppointmentBookingTrigger serviceIds={[service.id]}>
            <Button>Book this service</Button>
          </AppointmentBookingTrigger>
          <Button variant="outline" asChild>
            <Link href="/services">All services</Link>
          </Button>
        </div>
      </div>
    </PageLayout>
  )
}
